import useJDialog from '@/hooks/useJDialog';
import useJSnackbar from '@/hooks/useJSnackbar';
import { TopBar } from '@/layouts/TopBar';
import Paths from '@/routes/paths';
import { JButton } from '@atoms/JButton';
import Jpaper from '@atoms/JPaper';
import { JVideoPlayer } from '@atoms/JVideoPlayer';
import { Loading } from '@atoms/Loading';
import { StatusChip } from '@atoms/StatusChip';
import { DisableDialogContent } from '@candidates/components/CandidateRow';
import UserInfos from '@candidates/components/UserInfos';
import { ExperienceCard } from '@candidates/components/atoms/ExperienceCard';
import { CvVideoStatuBar } from '@candidates/components/molecules/CvVideoStatuBar';
import { useDisableCandidate } from '@candidates/mutations/candidateMutations';
import { useGetCandidateById } from '@candidates/queries/useGetCandidates';
import CvVideoAcceptanceDialogBody from '@components/molucules/CvVideoAcceptanceDialogBody';
import CvVideoRejectionDialogBody from '@components/molucules/CvVideoRejectionDialogBody';
import { IconSize } from '@constants';
import CvVideoStatus from '@cv-videos/models/cv-video-status';
import AcceptanceCvVideoDto from '@cv-videos/models/dto/acceptance-cv-video-dto';
import { useEditCVVideoByIdMutation } from '@cv-videos/mutations/cvVideoMutations';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

import ExperienceTypeEnum from '@models/experience-type.enum';
import { Box, Chip, Grid, Menu, Stack, Typography } from '@mui/material';
import { MenuItemStyled } from '@styled/menu-item.styled';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

type disbaleMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string },
  unknown
>;

const CandidateDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSnackbar } = useJSnackbar();
  const {
    mutate: disableCandidate,
    isLoading: isDisableCandidateLoading,
    isSuccess: isDisableCandidateSuccess,
  } = useDisableCandidate();
  const {
    mutate: mutateCvVideo,
    isLoading: isEditCvVideoLoading,
    isSuccess: isEditCvVideoSuccess,
  } = useEditCVVideoByIdMutation();
  const [rowMenuanchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const open = Boolean(rowMenuanchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRowMenuAnchorEl(event.currentTarget);
  };
  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
  };
  const { data } = useGetCandidateById(id ?? '');
  const candidate = data?.data;
  const user = candidate?.user;
  const [selectedCvVideoId, setSelectedCvVideoId] = useState<
    string | undefined
  >();
  const navigate = useNavigate();
  const { showJDialog, closeJDialog } = useJDialog();

  const selectedCVVideo = candidate?.cvVideos.find(
    (item) => item.id === selectedCvVideoId,
  );
  useEffect(() => {
    if (selectedCvVideoId) return;
    setSelectedCvVideoId(candidate?.cvVideos[0]?.id);
  }, [candidate?.cvVideos]);

  const experiences = candidate?.experiences.filter(
    (item) => item.type === ExperienceTypeEnum.PROFESSIONAL,
  );

  const sortedExperiences =
    experiences?.slice().sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);

      return dateB.getTime() - dateA.getTime();
    }) ?? [];

  const formations = candidate?.formations;
  const sortedFormations =
    formations?.slice().sort((a, b) => {
      const dateA = new Date(a.startDate);
      const dateB = new Date(b.startDate);

      return dateB.getTime() - dateA.getTime();
    }) ?? [];

  const handleAccept = useCallback(() => {
    if (!selectedCvVideoId) return;
    mutateCvVideo({
      id: selectedCvVideoId,
      payload: { status: CvVideoStatus.ACCEPTED },
    });
  }, [selectedCvVideoId]);

  const handleReject = useCallback(
    (value: AcceptanceCvVideoDto) => {
      if (!selectedCvVideoId) return;
      mutateCvVideo({
        id: selectedCvVideoId,
        payload: value,
      });
    },
    [selectedCvVideoId],
  );

  const handleDisableCandidate = useCallback(() => {
    if (!id) return;
    disableCandidate({ id });
  }, [id]);

  const showAcceptanceDialog = () => {
    showJDialog(
      t('WHY_ACCEPT'),
      <CvVideoAcceptanceDialogBody
        candidateName={`${user?.firstname} ${user?.lastname}`}
        onClose={closeJDialog}
        onConfirm={handleAccept}
        cvVideoName={selectedCVVideo?.name}
      />,
      'basic',
    );
  };

  const showRejectionDialog = () => {
    showJDialog(
      t('WHY_REJECT'),
      <CvVideoRejectionDialogBody
        candidateName={`${user?.firstname} ${user?.lastname}`}
        onClose={closeJDialog}
        onConfirm={handleReject}
      />,
      'basic',
    );
  };

  const openDisableCandidateDialog = (
    candidateTitle: string,
    candidateId: string,
    disableMutation: disbaleMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      t('DISABLE_CANDIDATE_TITLE'),
      <DisableDialogContent
        candidateId={candidateId}
        candidateTitle={candidateTitle}
        disableMutation={disableMutation}
      />,
    );
  };
  useEffect(() => {
    if (!isEditCvVideoSuccess) return;
    queryClient.invalidateQueries(['candidate', id]);
    showSnackbar(t('CV_VIDEO_STATUS_UPDATED'), 'success');
    closeJDialog();
    handleRowMenuClose();
  }, [isEditCvVideoSuccess]);
  useEffect(() => {
    if (!isDisableCandidateSuccess) return;
    closeJDialog();
    queryClient.invalidateQueries(['candidate', id]);
    navigate(-1);
    showSnackbar(t('CANDIDATE_UPDATE_SUCCESS'), 'success');
    handleRowMenuClose();
  }, [isDisableCandidateSuccess]);
  return (
    <Stack data-qa={`candidate ${id}`}>
      {isEditCvVideoLoading || (isDisableCandidateLoading && <Loading />)}
      <TopBar
        navigateToBack={() => navigate(Paths.CandidatePage)}
        title={
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography variant="h2">{`${user?.firstname} ${user?.lastname}`}</Typography>
            <StatusChip
              label={!candidate?.deletedAt ? t('ACTIVE') : t('INACTIVE')}
              variant={!candidate?.deletedAt ? 'success' : 'danger'}
            />
          </Stack>
        }
      >
        <JButton
          color="inherit"
          onClick={handleClick}
          endIcon={
            !open ? (
              <ChevronDownIcon height={IconSize.buttonIcon} />
            ) : (
              <ChevronUpIcon height={IconSize.buttonIcon} />
            )
          }
        >
          {t('ACTIONS')}
        </JButton>
        <Menu
          data-qa="top-bar-menu"
          id="basic-menu"
          anchorEl={rowMenuanchorEl}
          open={open}
          onClose={handleRowMenuClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItemStyled
            red={false}
            green={false}
            data-qa={'navigation'}
            onClick={() =>
              selectedCvVideoId &&
              navigate(Paths.CvVideoDetails.replace(':id', selectedCvVideoId))
            }
          >
            {t('MANAGE_CV_VIDEO')}
          </MenuItemStyled>
          {selectedCVVideo?.status !== CvVideoStatus.ACCEPTED && (
            <MenuItemStyled
              red={false}
              green={true}
              data-qa={'accept'}
              onClick={showAcceptanceDialog}
            >
              {t('ACCEPT_CV_VIDEO')}
            </MenuItemStyled>
          )}

          {selectedCVVideo?.status !== CvVideoStatus.REJECTED && (
            <MenuItemStyled
              red={true}
              green={false}
              data-qa={'reject'}
              onClick={showRejectionDialog}
            >
              {t('REJECT_CV_VIDEO')}
            </MenuItemStyled>
          )}

          <MenuItemStyled
            red={true}
            green={false}
            data-qa={'disable-profile'}
            onClick={() =>
              candidate &&
              openDisableCandidateDialog(
                `${candidate?.user.firstname} ${candidate?.user.lastname}`,
                candidate?.id,
                handleDisableCandidate,
              )
            }
          >
            {t('DISABLE_PROFILE')}
          </MenuItemStyled>
        </Menu>
      </TopBar>
      <Stack gap={2} px={4} mb={4}>
        <Grid
          component={'div'}
          container
          mt={2}
          spacing={4}
          alignItems={'stretch'}
        >
          <Grid item md={4}>
            <UserInfos
              user={user}
              cvFileUrl={candidate?.cvFileUrl}
              headline={candidate?.headline}
            />
          </Grid>
          <Grid item md={8} position="relative">
            {selectedCVVideo && (
              <>
                <CvVideoStatuBar
                  selectedId={selectedCvVideoId ?? ''}
                  cvVideoStatus={selectedCVVideo?.status}
                  title={selectedCVVideo.name}
                  cvVideos={candidate?.cvVideos ?? []}
                  onSelect={(val) => setSelectedCvVideoId(val)}
                />
                <JVideoPlayer
                  key={selectedCvVideoId}
                  thumbnailUrl={selectedCVVideo.thumbnailUrl}
                  height={519}
                  videoUrl={selectedCVVideo.videoUrl}
                />
              </>
            )}
          </Grid>
        </Grid>

        <Jpaper>
          <Typography variant="h4" gutterBottom>
            {t('KEYWORDS')}
          </Typography>
          <Stack data-qa="keywords" direction={'row'} gap={1}>
            {candidate?.tags?.map((item) => (
              <Chip
                data-qa={item}
                key={item}
                label={`${item?.charAt(0).toUpperCase()}${item?.slice(1)}`}
                variant="filled"
              />
            ))}
          </Stack>
        </Jpaper>

        <Grid component={'div'} container spacing={4}>
          <Grid item md={4}>
            <Jpaper attr={{ fullHeight: true }}>
              <Typography variant="h3" mb={3}>
                {t('PROFESSIONAL_INFOS')}
              </Typography>
              <Stack gap={4}>
                <Box>
                  <Typography data-qa="sector-title" variant="h4" gutterBottom>
                    {t('SECTOR')}
                  </Typography>
                  <Typography data-qa="sector">
                    {candidate?.ca_activityAreas}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    data-qa="qualification-title"
                    variant="h4"
                    gutterBottom
                  >
                    {t('QUALIFICATIONS')}
                  </Typography>
                  <Stack
                    data-qa="qualification"
                    direction={'row'}
                    gap={1}
                    flexWrap={'wrap'}
                  >
                    {candidate?.ca_qualifications?.map((item) => (
                      <Chip
                        data-qa={`q-${item}`}
                        key={item}
                        label={`${item?.charAt(0).toUpperCase()}${item?.slice(
                          1,
                        )}`}
                        variant="filled"
                      />
                    ))}
                  </Stack>
                </Box>

                {candidate?.yearsExperience && (
                  <Box>
                    <Typography data-qa="years-title" variant="h4" gutterBottom>
                      {t('YEAR_EXPERIENCE')}
                    </Typography>
                    <Typography data-qa="years">
                      {candidate?.yearsExperience} {t('YEARS')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Jpaper>
          </Grid>
          {sortedExperiences.length > 0 && (
            <Grid item md={4}>
              <Jpaper attr={{ fullHeight: true }}>
                <Typography variant="h3" mb={3} data-qa="experience-title">
                  {t('EXPERIENCES')}
                </Typography>
                <Stack gap={2}>
                  {sortedExperiences?.map((item, index) => (
                    <ExperienceCard
                      data-qa={item.name}
                      title={item.name}
                      establishment={item.establishment}
                      startDate={item.startDate}
                      endDate={item.endDate}
                      description={item.description}
                      key={item.id}
                      withDivider={sortedExperiences?.length - 1 !== index}
                    />
                  ))}
                </Stack>
              </Jpaper>
            </Grid>
          )}

          {sortedFormations.length > 0 && (
            <Grid item md={4}>
              <Jpaper attr={{ fullHeight: true }}>
                <Typography variant="h3" mb={3} data-qa="trainings-title">
                  {t('TRAININGS')}
                </Typography>
                <Stack gap={2}>
                  {sortedFormations?.map((item, index) => (
                    <ExperienceCard
                      data-qa={item.name}
                      title={item.name}
                      establishment={item.establishment}
                      startDate={item.startDate}
                      endDate={item.endDate}
                      description={item.description}
                      key={item.id}
                      withDivider={sortedFormations?.length - 1 !== index}
                    />
                  ))}
                </Stack>
              </Jpaper>
            </Grid>
          )}
        </Grid>
      </Stack>
    </Stack>
  );
};

export default CandidateDetails;
